import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import request from './request';
import { DATA_QUERY_EXPIRATION, QUERY_RETRY } from './queries';
import { CustomerRole } from 'src/contexts/AuthContext';

// --- Types ---

export type UserStatus = 'active' | 'pending' | 'inactive';

export type ManagedUser = {
  id: string;
  email: string;
  displayName: string | null;
  role: CustomerRole;
  status: UserStatus;
  joinedAt: string | null;
  invitedAt: string | null;
};

type UsersResponse = {
  users: ManagedUser[];
};

type InviteUserVariables = {
  email: string;
  role: CustomerRole;
  displayName?: string;
};

type UpdateUserVariables = {
  userId: string;
  email?: string;
  displayName?: string;
  role?: CustomerRole;
};

export type BulkAction = 'delete' | 'resend_invites';

type BulkUsersVariables = {
  action: BulkAction;
  userIds: string[];
};

type BulkOperationResponse = {
  success: boolean;
  processed: number;
  errors: string[];
};

type ResendInviteResponse = {
  message: string;
  newUserId: string;
};

// --- Query Keys ---

const usersQueryKey = (customer: string) =>
  ['useUsersQuery', customer] as const;

// --- Query Hooks ---

export function useUsersQuery(customer: string) {
  const queryId = useMemo(() => usersQueryKey(customer), [customer]);

  return useQuery(
    queryId,
    async () => {
      const response = await request.get<UsersResponse>('/data/users', {
        params: { customer },
      });
      return response.data?.users ?? [];
    },
    {
      retry: QUERY_RETRY,
      staleTime: DATA_QUERY_EXPIRATION,
      enabled: Boolean(customer),
    },
  );
}

// --- Mutation Hooks ---

export function useInviteUserMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async (variables: InviteUserVariables) => {
      const response = await request.post<ManagedUser>(
        '/data/users/invite',
        variables,
        { params: { customer } },
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(usersQueryKey(customer));
      },
    },
  );
}

export function useUpdateUserMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ userId, email, displayName, role }: UpdateUserVariables) => {
      const response = await request.patch<ManagedUser>(
        '/data/users/user',
        { email, displayName, role },
        { params: { customer, user_id: userId } },
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(usersQueryKey(customer));
      },
    },
  );
}

export function useDeleteUserMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async (userId: string) => {
      await request.delete('/data/users/user', {
        params: { customer, user_id: userId },
      });
      return userId;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(usersQueryKey(customer));
      },
    },
  );
}

export function useResendInviteMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async (userId: string) => {
      const response = await request.post<ResendInviteResponse>(
        '/data/users/resend-invite',
        null,
        { params: { customer, user_id: userId } },
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(usersQueryKey(customer));
      },
    },
  );
}

export function useBulkUsersMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async (variables: BulkUsersVariables) => {
      const response = await request.post<BulkOperationResponse>(
        '/data/users/bulk',
        variables,
        { params: { customer } },
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(usersQueryKey(customer));
      },
    },
  );
}
